import { useFolders, useNotes, useNowContext } from "@hq/client-react";
import type { CalendarDate, Note } from "@hq/contracts";
import { addDays, dailyNoteTitle, daysBetween, type NowContext, todayOf } from "@hq/core";
import { Button, cn } from "@z/ui";
import {
  type Dispatch,
  type ReactNode,
  type Ref,
  type SetStateAction,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { DailyWritingSurface } from "./DailyWritingSurface";

export interface DailyNotesHandle {
  /** Scrolls the day's heading to the top of the pane, extending the range to reach it. */
  scrollToDate(date: CalendarDate): void;
  /** Scrolls to the day and focuses its editor (or its empty placeholder). */
  focusDate(date: CalendarDate): void;
}

export interface DailyNotesProps {
  renderDayFooter?: ((date: CalendarDate, note: Note | undefined) => ReactNode) | undefined;
  /** Focus the initial day's editor once it mounts. */
  autoFocus?: boolean;
  /** The day scrolled to the top on mount. Defaults to today. */
  initialDate?: CalendarDate;
  /** Fixed clock for tests; defaults to the live `useNowContext()`. */
  now?: NowContext;
  className?: string;
  ref?: Ref<DailyNotesHandle>;
}

export type DayRange = { start: CalendarDate; end: CalendarDate };

/** Days shown around the initial day. */
export const DAYS_BEFORE = 7;
export const DAYS_AFTER = 2;
/** Days added per "load earlier/later". */
export const LOAD_STEP = 7;
/** Upper bound on mounted days: extending one end drops days from the other (a sliding window). */
export const MAX_DAYS = 24;

export const DAILY_NOTE_TITLE = /^\d{4}-\d{2}-\d{2}$/;
export const JOURNAL_FOLDER_NAME = "journal";

export function initialRange(anchor: CalendarDate): DayRange {
  return { start: addDays(anchor, -DAYS_BEFORE), end: addDays(anchor, DAYS_AFTER) };
}

export function listDays({ start, end }: DayRange): CalendarDate[] {
  const days: CalendarDate[] = [];
  for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
  return days;
}

/** Grows the range by `LOAD_STEP` at one end, trimming the other end to `MAX_DAYS`. */
export function shiftRange(range: DayRange, direction: "earlier" | "later"): DayRange {
  if (direction === "earlier") {
    const start = addDays(range.start, -LOAD_STEP);
    const overflow = daysBetween(start, range.end) + 1 - MAX_DAYS;
    return { start, end: overflow > 0 ? addDays(range.end, -overflow) : range.end };
  }
  const end = addDays(range.end, LOAD_STEP);
  const overflow = daysBetween(range.start, end) + 1 - MAX_DAYS;
  return { start: overflow > 0 ? addDays(range.start, overflow) : range.start, end };
}

/** The smallest range containing both the current one and `date`, trimmed to `MAX_DAYS` around `date`. */
export function rangeIncluding(range: DayRange, date: CalendarDate): DayRange {
  if (date >= range.start && date <= range.end) return range;
  if (date < range.start) {
    const end =
      daysBetween(date, range.end) + 1 > MAX_DAYS ? addDays(date, MAX_DAYS - 1) : range.end;
    return { start: date, end };
  }
  const start =
    daysBetween(range.start, date) + 1 > MAX_DAYS ? addDays(date, 1 - MAX_DAYS) : range.start;
  return { start, end: date };
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const shortDayFormat = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** `2026-09-16` → `Wed, September 16th, 2026`. */
export function formatDayHeading(date: CalendarDate): string {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7));
  const d = Number(date.slice(8, 10));
  const weekday = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${weekday}, ${MONTHS[m - 1]} ${ordinal(d)}, ${y}`;
}

type DaySectionHandle = { focus(): void; element: HTMLElement | null };

interface DaySectionProps {
  renderDayFooter?: ((date: CalendarDate, note: Note | undefined) => ReactNode) | undefined;
  date: CalendarDate;
  note: Note | undefined;
  folderId: string | undefined;
  isToday: boolean;
  autoFocus: boolean;
  ref?: Ref<DaySectionHandle>;
}

/** Each date keeps one writing surface mounted while its note is created and saved. */
function DaySection({
  date,
  note,
  folderId,
  isToday,
  autoFocus,
  ref,
  renderDayFooter,
}: DaySectionProps) {
  const headingId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const editorRef = useRef<{ focus(): void }>(null);
  const heading = formatDayHeading(date);
  const shortDate = shortDayFormat.format(new Date(`${date}T00:00:00Z`));
  useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.focus(),
    get element() {
      return sectionRef.current;
    },
  }));
  return (
    <section
      ref={sectionRef}
      data-daily-date={date}
      data-daily-today={isToday ? "" : undefined}
      aria-labelledby={headingId}
      className="px-6"
    >
      <div
        className={cn(
          "mx-auto flex w-full max-w-[72ch] flex-col gap-3 border-b border-border/40",
          isToday ? "py-6" : "py-4",
        )}
      >
        <DailyWritingSurface
          date={date}
          note={note}
          folderId={folderId}
          label={`Note for ${heading}`}
          autoFocus={autoFocus}
          isToday={isToday}
          heading={
            <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-1">
              <h2
                id={headingId}
                aria-label={heading}
                aria-current={isToday ? "date" : undefined}
                className={cn(
                  "font-semibold tracking-tight",
                  isToday ? "text-xl text-accent" : "text-base text-fg",
                )}
              >
                {isToday ? "Today" : shortDate}
              </h2>
              {isToday && (
                <time dateTime={date} className="text-sm text-fg-muted">
                  {shortDate}
                </time>
              )}
              {!isToday && (
                <time dateTime={date} className="text-xs text-fg-faint">
                  {date.slice(0, 4)}
                </time>
              )}
            </div>
          }
          ref={editorRef}
        />
        {renderDayFooter?.(date, note)}
      </div>
    </section>
  );
}

type Pending = {
  /** Keep this section where it is on screen after the range changed. */
  anchor?: { date: CalendarDate; top: number };
  scroll?: CalendarDate;
  focus?: CalendarDate;
};

function extendRange(
  container: HTMLElement | null,
  pending: Pending,
  setRange: Dispatch<SetStateAction<DayRange>>,
  direction: "earlier" | "later",
): void {
  if (container) {
    const sections = container.querySelectorAll<HTMLElement>("[data-daily-date]");
    const keep = direction === "earlier" ? sections[0] : sections[sections.length - 1];
    const date = keep?.dataset["dailyDate"];
    if (keep && date) pending.anchor = { date, top: keep.getBoundingClientRect().top };
  }
  setRange((prev) => shiftRange(prev, direction));
}

/**
 * Reflect-style day list: one section per calendar day, today first in view, earlier days above
 * and later days below. Each day is a plain note titled `YYYY-MM-DD` in the Journal folder
 * (when one exists; otherwise the root), created only when the user types into it.
 */
export function DailyNotes({
  renderDayFooter,
  autoFocus = false,
  initialDate,
  now,
  className,
  ref,
}: DailyNotesProps) {
  const liveNow = useNowContext();
  const today = todayOf(now ?? liveNow);
  const anchorDate = initialDate ?? today;
  const [range, setRange] = useState(() => initialRange(anchorDate));
  const { data: notes } = useNotes();
  const { data: folders } = useFolders();
  const containerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef(new Map<CalendarDate, DaySectionHandle>());
  const pendingRef = useRef<Pending>({});

  const journal = folders?.find(
    (f) => f.deletedAt === undefined && f.name.trim().toLowerCase() === JOURNAL_FOLDER_NAME,
  );
  const notesByDate = new Map<string, Note>();
  for (const note of notes ?? []) {
    if (DAILY_NOTE_TITLE.test(note.title) && !notesByDate.has(note.title)) {
      notesByDate.set(note.title, note);
    }
  }
  const days = listDays(range);

  const goTo = (date: CalendarDate, focus: boolean) => {
    pendingRef.current.scroll = date;
    if (focus) pendingRef.current.focus = date;
    // Same-range navigation still needs the layout pass below; a fresh object forces it.
    setRange((prev) => ({ ...rangeIncluding(prev, date) }));
  };

  useImperativeHandle(ref, () => ({
    scrollToDate: (date) => goTo(date, false),
    focusDate: (date) => goTo(date, true),
  }));

  // Scroll positioning is the one thing React cannot express declaratively: keep the anchored
  // section still after the range changed, then honour a requested scroll/focus. Runs after
  // every commit; the pending flags make it a no-op unless something was requested.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const pending = pendingRef.current;
    if (!container) return;
    if (pending.anchor) {
      const el = sectionsRef.current.get(pending.anchor.date)?.element;
      // Native scroll anchoring may already have compensated for inserted/removed
      // days. Correct only the remaining viewport movement, not the document offset.
      if (el) {
        const delta = el.getBoundingClientRect().top - pending.anchor.top;
        container.scrollTop += delta;
      }
      delete pending.anchor;
    }
    if (pending.scroll) {
      const el = sectionsRef.current.get(pending.scroll)?.element;
      if (el) container.scrollTop = el.offsetTop - (toolbarRef.current?.offsetHeight ?? 0);
      delete pending.scroll;
    }
    if (pending.focus) {
      sectionsRef.current.get(pending.focus)?.focus();
      delete pending.focus;
    }
  });

  // On mount (and when the initial day changes) put that day's heading at the top.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const el = sectionsRef.current.get(anchorDate)?.element;
    if (container && el)
      container.scrollTop = el.offsetTop - (toolbarRef.current?.offsetHeight ?? 0);
  }, [anchorDate]);

  // The sentinels extend the range as they scroll into view; the buttons do the same by hand.
  useEffect(() => {
    const root = containerRef.current;
    const top = topRef.current;
    const bottom = bottomRef.current;
    if (!root || !top || !bottom || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const direction = entry.target === top ? "earlier" : "later";
          extendRange(root, pendingRef.current, setRange, direction);
        }
      },
      { root, rootMargin: "240px 0px" },
    );
    observer.observe(top);
    observer.observe(bottom);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      data-daily-notes=""
      className={cn("relative h-full min-h-0 w-full overflow-y-auto bg-bg text-fg", className)}
    >
      <div
        ref={toolbarRef}
        className="sticky top-0 z-sticky flex h-11 items-center justify-between border-b border-border/60 bg-bg px-6"
      >
        <span className="text-xs font-medium text-fg-muted">Daily journal</span>
        <Button variant="ghost" size="sm" onClick={() => goTo(today, true)}>
          Jump to today
        </Button>
      </div>
      <div ref={topRef} className="flex justify-center py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => extendRange(containerRef.current, pendingRef.current, setRange, "earlier")}
        >
          Load earlier days
        </Button>
      </div>
      {days.map((date) => (
        <DaySection
          key={date}
          renderDayFooter={renderDayFooter}
          date={date}
          note={notesByDate.get(dailyNoteTitle(date))}
          folderId={journal?.id}
          isToday={date === today}
          autoFocus={autoFocus && date === anchorDate}
          ref={(handle) => {
            if (handle) sectionsRef.current.set(date, handle);
            return () => {
              sectionsRef.current.delete(date);
            };
          }}
        />
      ))}
      <div ref={bottomRef} className="flex justify-center py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => extendRange(containerRef.current, pendingRef.current, setRange, "later")}
        >
          Load later days
        </Button>
      </div>
    </div>
  );
}
